import * as THREE from "three";

/**
 * Procedural canvas textures for floor decals (W18).
 *
 * Same fallback contract as `doorTextures.ts`: these draw immediately so
 * the runtime always has something to render, while authored PNGs from
 * `public/<adventure>/world/floor-decals/<id>/<state>.png`
 * load asynchronously and replace the fallback once available.
 */

export type FloorDecalKind =
  | "trap-armed"
  | "trap-disarmed"
  | "trap-triggered"
  | "teleporter"
  | "stairs-down"
  | "stairs-up"
  | "camp";

const TEX_SIZE = 128;

const cache = new Map<FloorDecalKind, THREE.CanvasTexture>();

export function getFloorDecalTexture(kind: FloorDecalKind): THREE.CanvasTexture {
  const cached = cache.get(kind);
  if (cached) return cached;
  const tex = draw(kind);
  cache.set(kind, tex);
  return tex;
}

function draw(kind: FloorDecalKind): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = TEX_SIZE;
  canvas.height = TEX_SIZE;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("floorDecalTextures: 2D context unavailable");

  ctx.clearRect(0, 0, TEX_SIZE, TEX_SIZE);

  switch (kind) {
    case "trap-armed":
      drawTrapArmed(ctx);
      break;
    case "trap-disarmed":
      drawTrapDisarmed(ctx);
      break;
    case "trap-triggered":
      drawTrapTriggered(ctx);
      break;
    case "teleporter":
      drawTeleporter(ctx);
      break;
    case "stairs-down":
      drawStairs(ctx, "down");
      break;
    case "stairs-up":
      drawStairs(ctx, "up");
      break;
    case "camp":
      drawCamp(ctx);
      break;
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.needsUpdate = true;
  return tex;
}

function drawTrapArmed(ctx: CanvasRenderingContext2D): void {
  // Faint reddish jagged ring — "you see something here, watch out".
  const cx = TEX_SIZE / 2;
  const cy = TEX_SIZE / 2;
  ctx.strokeStyle = "#a83232";
  ctx.lineWidth = 3;
  ctx.beginPath();
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const r = i % 2 === 0 ? 38 : 28;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.stroke();
}

function drawTrapDisarmed(ctx: CanvasRenderingContext2D): void {
  // Same shape, muted gray.
  const cx = TEX_SIZE / 2;
  const cy = TEX_SIZE / 2;
  ctx.strokeStyle = "#555";
  ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * Math.PI * 2;
    const r = i % 2 === 0 ? 38 : 28;
    const x = cx + Math.cos(a) * r;
    const y = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.stroke();
  ctx.strokeStyle = "#888";
  ctx.beginPath();
  ctx.moveTo(cx - 22, cy - 22);
  ctx.lineTo(cx + 22, cy + 22);
  ctx.moveTo(cx + 22, cy - 22);
  ctx.lineTo(cx - 22, cy + 22);
  ctx.stroke();
}

function drawTrapTriggered(ctx: CanvasRenderingContext2D): void {
  // Scorched circle.
  const cx = TEX_SIZE / 2;
  const cy = TEX_SIZE / 2;
  const grad = ctx.createRadialGradient(cx, cy, 4, cx, cy, 44);
  grad.addColorStop(0, "rgba(60,30,10,0.95)");
  grad.addColorStop(1, "rgba(20,10,5,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy, 48, 0, Math.PI * 2);
  ctx.fill();
}

function drawTeleporter(ctx: CanvasRenderingContext2D): void {
  // Concentric magenta rings.
  const cx = TEX_SIZE / 2;
  const cy = TEX_SIZE / 2;
  for (let r = 18; r <= 50; r += 8) {
    ctx.strokeStyle = `rgba(200,80,220,${(60 - r) / 60})`;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.stroke();
  }
}

function drawStairs(ctx: CanvasRenderingContext2D, dir: "up" | "down"): void {
  // A chunky chevron pointing up (stairs up) or down (stairs down).
  const cx = TEX_SIZE / 2;
  const cy = TEX_SIZE / 2;
  ctx.fillStyle = dir === "down" ? "#2a2a2a" : "#888";
  // Outer step plate.
  ctx.fillRect(cx - 44, cy - 44, 88, 88);
  // Chevron arrows.
  ctx.fillStyle = dir === "down" ? "#888" : "#222";
  for (let i = 0; i < 3; i++) {
    const offset = (i - 1) * 18;
    ctx.beginPath();
    if (dir === "down") {
      ctx.moveTo(cx - 24, cy + offset - 10);
      ctx.lineTo(cx, cy + offset + 6);
      ctx.lineTo(cx + 24, cy + offset - 10);
      ctx.lineTo(cx + 18, cy + offset - 14);
      ctx.lineTo(cx, cy + offset - 2);
      ctx.lineTo(cx - 18, cy + offset - 14);
    } else {
      ctx.moveTo(cx - 24, cy - offset + 10);
      ctx.lineTo(cx, cy - offset - 6);
      ctx.lineTo(cx + 24, cy - offset + 10);
      ctx.lineTo(cx + 18, cy - offset + 14);
      ctx.lineTo(cx, cy - offset + 2);
      ctx.lineTo(cx - 18, cy - offset + 14);
    }
    ctx.closePath();
    ctx.fill();
  }
}

function drawCamp(ctx: CanvasRenderingContext2D): void {
  // Campfire: a ring of gray stones around banked embers, with a bedroll
  // laid alongside — "you can rest here", readable from a cell away.
  const cx = TEX_SIZE / 2;
  const cy = TEX_SIZE / 2;

  // Bedroll — a muted brown rectangle with a lighter pillow end.
  ctx.fillStyle = "#5a4632";
  ctx.fillRect(cx - 52, cy + 22, 74, 24);
  ctx.fillStyle = "#7a6248";
  ctx.fillRect(cx - 52, cy + 22, 18, 24);

  // Stone ring.
  ctx.fillStyle = "#777";
  for (let i = 0; i < 8; i++) {
    const a = (i / 8) * Math.PI * 2;
    const x = cx + Math.cos(a) * 26;
    const y = cy - 12 + Math.sin(a) * 20;
    ctx.beginPath();
    ctx.arc(x, y, 6, 0, Math.PI * 2);
    ctx.fill();
  }

  // Embers + flame.
  const grad = ctx.createRadialGradient(cx, cy - 12, 2, cx, cy - 12, 16);
  grad.addColorStop(0, "#ffd75e");
  grad.addColorStop(0.5, "#e07820");
  grad.addColorStop(1, "rgba(120,40,10,0)");
  ctx.fillStyle = grad;
  ctx.beginPath();
  ctx.arc(cx, cy - 12, 16, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#ffb63e";
  ctx.beginPath();
  ctx.moveTo(cx - 7, cy - 8);
  ctx.quadraticCurveTo(cx - 2, cy - 26, cx + 1, cy - 30);
  ctx.quadraticCurveTo(cx + 3, cy - 20, cx + 8, cy - 10);
  ctx.closePath();
  ctx.fill();
}
